/* eslint-disable react/prop-types, camelcase */
import React, { useState, useEffect } from "react";
import { Flex, Form, InputNumber } from "antd";
import { useNewTrialState } from "../../../pages/new-trail/hooks/NewTrialStateProvider";
import "./style.less";
import { restrictToTwoDecimals } from "../../../pages/create-protocol/utils";
import { useTranslation } from "react-i18next";
import { formatTranslation } from "../../utils/translationUtils";

function resolveStandardBiologicalApplicationSettings(
  trialInfo,
  ratesDosage,
  fallback
) {
  const trialInfoSettings =
    trialInfo?.standard_field_properties
      ?.standard_biological_application_settings;
  if (Array.isArray(trialInfoSettings) && trialInfoSettings.length > 0) {
    return trialInfoSettings;
  }

  const ratesDosageSettings =
    ratesDosage?.standard_biological_application_settings;
  if (Array.isArray(ratesDosageSettings) && ratesDosageSettings.length > 0) {
    return ratesDosageSettings;
  }

  return fallback;
}

function resolveStandardTankMixRates(
  trialInfo,
  ratesDosage,
  biologicalProducts
) {
  const trialInfoRates =
    trialInfo?.standard_field_properties?.standard_tank_mix_rates;
  if (Array.isArray(trialInfoRates) && trialInfoRates.length > 0) {
    return trialInfoRates;
  }

  const ratesDosageRates = ratesDosage?.standard_tank_mix_rates;
  if (Array.isArray(ratesDosageRates) && ratesDosageRates.length > 0) {
    return ratesDosageRates;
  }

  const treatments = biologicalProducts?.treatments;
  if (Array.isArray(treatments) && treatments.length > 0) {
    return treatments.map((treatment) => treatment?.tank_mix_rate?.min ?? 0);
  }

  return [0];
}

const StandardBiologicalRate = ({ treatmentIndex, treatmentData }) => {
  const { t } = useTranslation();
  const {
    ratesDosage,
    trialInfo,
    setRatesDosage,
    trialProtocolData,
    setIsStandardDataValid,
    setRatesDosageValidation,
  } = useNewTrialState();
  const [biologicalProducts, setBiologicalProducts] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    if (trialProtocolData?.product_details?.biologicals) {
      setBiologicalProducts(trialProtocolData?.product_details?.biologicals);
    }
  }, [trialProtocolData]);

  // --- Biological Plot Validation Function ---
  const isBiologicalPlotValid = React.useCallback(() => {
    if (!trialProtocolData?.protocol_type?.includes("biological")) return true;
    const applications =
      trialProtocolData?.product_details?.biologicals?.applications ?? [];
    const num = applications?.length ? applications?.length : 1;
    const data = ratesDosage?.rates_and_dosages;
    if (!Array.isArray(data)) return false;
    for (const row of data) {
      for (const entry of row) {
        if (
          !entry ||
          !Array.isArray(entry?.bioTreatement) ||
          entry?.bioTreatement.length !== num
        ) {
          return false;
        }
      }
    }
    return true;
  }, [ratesDosage, trialProtocolData]);
  // Initialize form values when biologicalProducts or ratesDosage changes
  useEffect(() => {
    if (!biologicalProducts) return;

    const standardApplicationSetting = biologicalProducts.treatments.map(
      (product) => ({
        product: product?.products?.[0]?.variety ?? "",
        tank_application_volume: product?.tank_mix_rate?.min ?? 0,
        unit: product?.products?.[0]?.measure_unit ?? "",
      })
    );

    const standard_biological_application_settings =
      resolveStandardBiologicalApplicationSettings(
        trialInfo,
        ratesDosage,
        standardApplicationSetting
      );

    const standard_tank_mix_rates = resolveStandardTankMixRates(
      trialInfo,
      ratesDosage,
      biologicalProducts
    );

    setRatesDosage((prevRatesDosage) => ({
      ...prevRatesDosage,
      standard_biological_application_settings,
      standard_tank_mix_rates,
    }));
  }, [biologicalProducts, trialInfo, setRatesDosage]);

  // Tank volume validation
  useEffect(() => {
    if (!biologicalProducts) return;
    const tankVolume =
      ratesDosage?.standard_biological_application_settings?.[treatmentIndex]
        ?.tank_application_volume;

    const min = treatmentData?.tankMixRate?.min;
    const max = treatmentData?.tankMixRate?.max;
    if (
      tankVolume == null ||
      tankVolume === 0 ||
      String(tankVolume) === "" ||
      (min !== undefined && tankVolume < min) ||
      (max !== undefined && tankVolume > max)
    ) {
      setIsStandardDataValid(false);
    } else {
      setIsStandardDataValid(true);
    }
  }, [
    ratesDosage,
    biologicalProducts,
    setIsStandardDataValid,
    treatmentIndex,
    treatmentData,
  ]);

  // --- Combined Validation ---
  useEffect(() => {
    // You may have your own logic for isSeedPlotValid and isNutriendPlotValid
    const isSeedPlotValid = true;
    const isNutriendPlotValid = true;
    const biologicalValid = isBiologicalPlotValid();
    setRatesDosageValidation(
      isSeedPlotValid && isNutriendPlotValid && biologicalValid
    );
  }, [
    ratesDosage,
    trialProtocolData,
    isBiologicalPlotValid,
    setRatesDosageValidation,
  ]);

  const tankMixRate = treatmentData?.tankMixRate || { min: 0, max: 0 };
  const measureUnit =
    treatmentData?.products?.[0]?.unit ||
    biologicalProducts?.treatments?.[treatmentIndex]?.products?.[0]
      ?.measure_unit;

  const tankApplicationVolume =
    ratesDosage?.standard_tank_mix_rates?.[treatmentIndex] ?? tankMixRate.min;

  useEffect(() => {
    form.setFieldsValue({ tank_application_volume: tankApplicationVolume });
  }, [tankApplicationVolume, form]);

  const handletankRateChange = (tankVolume) => {
    if (tankVolume === null || tankVolume === undefined) return;
    const applicationData = {
      product: treatmentData?.products?.[0]?.name,
      tank_application_volume: tankVolume,
      unit: measureUnit,
    };
    const updatedSettings = Array.isArray(
      ratesDosage?.standard_biological_application_settings
    )
      ? [...ratesDosage.standard_biological_application_settings]
      : [];
    updatedSettings[treatmentIndex] = applicationData;
    setRatesDosage({
      ...ratesDosage,
      standard_biological_application_settings: updatedSettings,
      standard_tank_mix_rates: updatedSettings?.map((setting) =>
        setting?.tank_application_volume != null
          ? setting?.tank_application_volume
          : 0
      ),
    });
  };

  return (
    <div className="standard-seedrate-main biological-rate-main">
      <Flex justify="space-between">
        <div className="productRate-info application-info">
          <Form form={form} layout="vertical" className="tank_form">
            <Form.Item
              name="tank_application_volume"
              label={formatTranslation(
                t("newTrial.ratesDosage.biologicalRate.tankVolume")
              )}
              className="input-biologicalrate"
              rules={[
                {
                  validator: (_, value) => {
                    const min = tankMixRate.min;
                    const max = tankMixRate.max;
                    if (value === undefined || value === null || value === "") {
                      return Promise.reject(
                        new Error(
                          formatTranslation(
                            t("trials.biological.tankVol.error3")
                          )
                        )
                      );
                    }
                    if (value < min) {
                      return Promise.reject(
                        new Error(
                          formatTranslation(
                            t(
                              "newTrial.ratesDosage.biologicalRate.outOfRangeError",
                              { min, max }
                            )
                          )
                        )
                      );
                    }
                    if (value > max) {
                      return Promise.reject(
                        new Error(
                          formatTranslation(
                            t(
                              "newTrial.ratesDosage.biologicalRate.outOfRangeError",
                              { min, max }
                            )
                          )
                        )
                      );
                    }
                    return Promise.resolve();
                  },
                  validateTrigger: ["onBlur", "onChange"],
                },
              ]}
            >
              <InputNumber
                suffix={"L/ha"}
                precision={2}
                step={0.01}
                onChange={(tankVolume) => {
                  handletankRateChange(tankVolume);
                }}
                onBlur={() => {
                  form.validateFields(["tank_application_volume"]);
                }}
                onKeyDown={(e) => restrictToTwoDecimals(e)}
                className="input-number customized-input-number biological-rate-input"
              />
            </Form.Item>
          </Form>
          <div className="product-info-label">
            {formatTranslation(
              t("newTrial.ratesDosage.biologicalRate.tankRangeLabel", {
                min: tankMixRate.min,
                max: tankMixRate.max,
              })
            )}
          </div>
        </div>
      </Flex>
    </div>
  );
};

export default StandardBiologicalRate;
